// Promotional pricing, resolved from Salesforce rather than authored here.
//
// ── Why this exists ─────────────────────────────────────────────────────
// Promo prices used to live in fittingTypes.ts as `pricingLabelPromo` strings
// — thirteen of them across fittings and markets, each hardcoding both the
// promotional price and the flat fee, gated behind a hardcoded date window
// (`isPromoWindowActive`). Every promotion, every market, every price change
// was a code change and a deploy.
//
// None of that is necessary. `/promos/validate` returns a computed
// `discount.discountedValue` per fitting — verified against production
// 2026-08-20, where CUSTOMFIT returns $50 for Driver/Iron/Putter and $100 for
// Full Bag and Full Bag (No Putter). Salesforce applies the promotion's own
// rules, including classifying "Full Bag (No Putter)" as a full-bag variant,
// which no client-side string could infer. The flat fee comes from
// `/fittings.unitPrice`, per store.
//
// ── Cheapest wins ───────────────────────────────────────────────────────
// A visitor can have two promotions in play: the store's master code and their
// own (a referral, or one deep-linked via ?Web_Promo=). We validate each and
// display the cheaper, remembering which code won so the terms shown belong to
// the promotion actually being applied.
//
// ── Referral codes and the email ────────────────────────────────────────
// This changed under us, and the note it replaces is kept because the change
// is the point.
//
// Measured 2026-08-20, a referral code (POLI113429) returned no `discount` at
// all and was byte-identical with and without an `email`, so this module sent
// none. Re-measured against production 2026-09-01, both halves are false:
// referrals now return a computed `discount.discountedValue`, and the email
// decides eligibility. Same code, same store, same fitting —
//
//   ZULT222988   no email → REJECTED     with email → valid, £0
//   Zrih884765   no email → valid, £0    with email → REJECTED
//
// So the grid was pricing from a call that asked the wrong question, and which
// of the two answers it got was down to how each individual code happens to be
// configured. That is exactly the "sometimes right" behaviour CC reported: one
// referral priced the cards FREE, another left them at the standard offer.
//
// Master codes are not email-sensitive — CUSTOMFIT prices a US Full Bag at
// $100 with an email, without one, and with an unknown one.
//
// Sending the pair the way Schedule takes it ("CUSTOMFIT;POLI113429") returns
// valid: false — validate takes one code.
//
// So Salesforce will not price a referral, and a referral cannot win a
// numeric comparison. Left there, the grid showed the master's $100 beside
// terms promising a FREE fitting — the two halves of the same card
// disagreeing.
//
// ── Reading the offer from its own terms ────────────────────────────────
// The terms do state the offer, in CC's standard wording: "Get a FREE Club
// Champion club fitting with a custom equipment purchase". That is not a
// novel price we are inventing — it is the widget's ordinary non-promotional
// offer, already authored per country as `pricingLabel`
// ("FREE w/ custom club purchase or $400 flat fitting fee"). So this is a
// choice between two labels the widget already ships, not prose parsed into
// an amount.
//
// The match is deliberately narrow: it requires all of FREE, fitting, and
// custom equipment purchase in one clause, and only applies when Salesforce
// returned no computed discount. CUSTOMFIT's terms ("…full bag fitting for
// $100 with a custom equipment purchase…") carry no FREE and cannot match.
//
// This is an inference, and it is the one part of this module not measured
// from a returned number. If Salesforce ever prices referral codes, delete
// `freeFittingFromTerms` and the discount path covers it.

import { validatePromo } from '../api/client';
import { countryConfigOrDefault } from '../config/countries';
import type { PromoDiscount } from '../types';

/** One fitting's resolved price, and which promotion produced it. */
export interface ResolvedFittingPrice {
  /** Undiscounted price, straight from /fittings.unitPrice. */
  listPrice: number;
  /** Cheapest promotional price found, or null when no promotion applies. */
  promoPrice: number | null;
  /** The code that produced `promoPrice`. Null when no promotion applies. */
  winningCode: string | null;
  /** Terms for the winning code, when Salesforce returned any. */
  terms: string | null;
}

export interface ResolveInput {
  locationId: string;
  /** Fitting wire names and their list prices, from /fittings. */
  fittings: Array<{ name: string; unitPrice: number }>;
  /** Candidate codes, cheapest-wins. Blank/duplicate entries are ignored. */
  codes: Array<string | null | undefined>;
  /**
   * Slot to evaluate against. Promotions have windows, so this must be a real
   * time — the fitting grid renders before a slot is chosen, so callers pass a
   * synthetic near-future time, which is the pattern the Web_Promo deep-link
   * path already uses.
   */
  slotStart: string;
  /**
   * The customer's email, where one has been captured.
   *
   * Salesforce decides referral and PGA eligibility from it, so a pricing call
   * without one gets the answer to a different question. Optional because the
   * grid can render before Step 1 has a usable address; the caller should pass
   * it only once it is actually valid, so a half-typed address does not spend
   * calls against the endpoint's rate limit.
   */
  email?: string;
}

/**
 * A referral's offer, read from its terms, when Salesforce returned no
 * computed discount. Returns 0 for a free-fitting-with-purchase offer and
 * null for anything else — including anything we cannot read confidently.
 */
function freeFittingFromTerms(terms: string | undefined): number | null {
  if (!terms) return null;
  const t = terms.replace(/\s+/g, ' ');
  return /\bFREE\b[^.]{0,60}\bfitting\b[^.]{0,60}\bcustom equipment purchase\b/i.test(t)
    ? 0
    : null;
}

/** Cheapest discounted value in a response, or null when there isn't one. */
function priceFrom(discount: PromoDiscount | undefined, listPrice: number): number | null {
  if (!discount) return null;
  if (typeof discount.discountedValue === 'number') return discount.discountedValue;
  // Providers differ. Fall back to deriving from amount/percent so a
  // provider that reports the discount rather than the price still works.
  if (typeof discount.amount === 'number') return Math.max(0, listPrice - discount.amount);
  if (typeof discount.percent === 'number') return Math.max(0, listPrice * (1 - discount.percent / 100));
  return null;
}

/**
 * Resolve the cheapest available price for each fitting.
 *
 * Failures are swallowed per call: a promotion that errors simply does not
 * win, and the fitting falls back to its list price. Pricing must never be
 * able to take down the grid.
 */
export async function resolvePromoPricing(
  input: ResolveInput,
): Promise<Record<string, ResolvedFittingPrice>> {
  const codes = Array.from(
    new Set(
      input.codes
        .map((c) => (c ?? '').trim())
        .filter((c) => c !== '')
        .map((c) => c.toUpperCase()),
    ),
  );

  const out: Record<string, ResolvedFittingPrice> = {};
  for (const f of input.fittings) {
    out[f.name] = {
      listPrice: f.unitPrice,
      promoPrice: null,
      winningCode: null,
      terms: null,
    };
  }

  if (!codes.length || !input.fittings.length) return out;

  const ask = (code: string, fitting: { name: string; unitPrice: number }) =>
    validatePromo({
      promoCode: code,
      locationId: input.locationId,
      fittingTypeName: fitting.name,
      appointmentStartDateTime: input.slotStart,
      price: fitting.unitPrice,
      // Sent because eligibility now depends on it. See the note at the top of
      // this file: measured 2026-09-01, the same referral code is valid with an
      // email and rejected without, and vice versa for a PGA partner code. The
      // grid was pricing itself from the answer to a question it never asked
      // properly. Master codes are unaffected — CUSTOMFIT prices a US Full Bag
      // at $100 with or without one.
      ...(input.email ? { email: input.email } : {}),
    });

  /** Record a price for one fitting, keeping the cheapest seen. */
  const offer = (
    fittingName: string,
    price: number | null,
    code: string,
    res: { terms?: string; discount?: PromoDiscount },
  ) => {
    const current = out[fittingName];
    if (!current) return;
    if (price === null || price >= current.listPrice) return;
    // Cheapest wins. Ties keep the first code, which is the master —
    // preferring the house promotion when a referral saves nothing.
    if (current.promoPrice !== null && price >= current.promoPrice) return;
    current.promoPrice = price;
    current.winningCode = code;
    current.terms = res.terms?.trim() ? res.terms : current.terms;
  };

  // One code at a time. Each is probed against a single fitting first, and
  // only fans out when the answer actually varies per fitting.
  //
  // The first version asked every fitting about every code up front — eleven
  // fittings against two codes is twenty-two calls in one burst, and because
  // each failure was swallowed individually, a few dropped calls left the grid
  // internally inconsistent: a free Full Bag beside a $50 Putter, from the
  // same promotion. Fewer calls is the fix, but the real fix is that a flat
  // offer is now applied from one answer, so it cannot land on some cards and
  // not others.
  await Promise.all(
    codes.map(async (code) => {
      const [first, ...rest] = input.fittings;
      if (!first) return;

      let head;
      try {
        head = await ask(code, first);
      } catch {
        return; // See the doc comment: pricing must never break the grid.
      }
      if (!head.valid) return; // Invalid for one fitting is invalid for all.

      if (!head.discount) {
        // Salesforce declined to compute a price ("Discount determined at
        // fitting"). Verified across every wire name at a store: the response
        // does not vary by fitting, so the offer is flat and one answer
        // settles the whole menu. No further calls.
        const flat = freeFittingFromTerms(head.terms);
        if (flat === null) return;
        for (const f of input.fittings) offer(f.name, flat, code, head);
        return;
      }

      // A computed discount does vary by fitting — CUSTOMFIT is $50 on a
      // Driver and $100 on a Full Bag — so the rest have to be asked.
      offer(first.name, priceFrom(head.discount, first.unitPrice), code, head);
      await Promise.all(
        rest.map(async (f) => {
          try {
            const res = await ask(code, f);
            if (res.valid) offer(f.name, priceFrom(res.discount, f.unitPrice), code, res);
          } catch {
            // Swallowed deliberately — this fitting keeps its list price.
          }
        }),
      );
    }),
  );

  return out;
}

/**
 * The price to show for a fitting: the promotional price when one applies,
 * otherwise the list price.
 */
export function effectivePrice(resolved: ResolvedFittingPrice | undefined): number | null {
  if (!resolved) return null;
  return resolved.promoPrice ?? resolved.listPrice;
}

/**
 * Currency symbol for the market — NOT for the currency Salesforce returns.
 *
 * Salesforce reports `currency: "USD"` on UK stores whose prices are in
 * pounds: CUSTOMFITUK at Crawley comes back as
 * `{ discountedValue: 100, originalValue: 250, currency: "USD" }`, where the
 * numbers are GBP. Trusting that field printed a UK grid reading $200, $150
 * and $100 against British prices.
 *
 * The store's country is what determines denomination, and we already know it,
 * so the market decides and the field is ignored.
 */
function symbolFor(country: string | null | undefined): string {
  switch (countryConfigOrDefault(country).currency.toUpperCase()) {
    case 'GBP':
      return '£';
    default:
      // USD, CAD and AUD all present as $ in the existing labels; the market
      // is already unambiguous from the store the visitor chose.
      return '$';
  }
}

function money(amount: number, country: string | null | undefined): string {
  const rounded = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${symbolFor(country)}${rounded}`;
}

/**
 * The card's price lines, built straight from resolved values.
 *
 * Deliberately not routed through `formatPricingLines`. That parser serves the
 * authored labels, where "FREE w/ custom club purchase or $400 flat fitting
 * fee" is the whole offer and both halves belong on the card. A promotional
 * price is a different thing: it stands alone. Naming the unconditional price
 * beside it undercuts the promotion at the moment the customer is choosing,
 * and it is not what was asked for — the ask was to state the purchase
 * condition on the promotional price, which line 1 does.
 *
 * That already held for a numeric promotional price. It now holds for a free
 * one too, which the string round-trip could not express: the parser keeps
 * both lines for anything spelled FREE, because it cannot tell an authored
 * FREE from a resolved one.
 *
 * Returns null when no promotion applies, so callers fall back to the authored
 * label and its parser.
 */
export function promoPricingLines(
  resolved: ResolvedFittingPrice | undefined,
  showFlatFee = false,
  country?: string | null,
): { line1: { bold: string; rest: string }; line2: { bold: string; rest: string } } | null {
  if (!resolved || resolved.promoPrice === null) return null;
  if (resolved.promoPrice >= resolved.listPrice) return null;

  // A free fitting is spelled FREE, not $0.
  const bold =
    resolved.promoPrice === 0 ? 'FREE' : money(resolved.promoPrice, country);

  return {
    line1: { bold, rest: ' - Fitting fee w/ custom club purchase' },
    // Whether the unconditional fee belongs beside the promotional price is a
    // market decision, not a property of the price — see
    // promoPriceShowsFlatFee() in config/countries.ts.
    line2: showFlatFee
      ? {
          bold: money(resolved.listPrice, country),
          rest: ' - Fitting fee w/out custom club purchase',
        }
      : { bold: '', rest: '' },
  };
}
